/**
 *  Customers counts aggregated memoized Selectors
 */

import { createSelector } from '@reduxjs/toolkit';

import { getSelectedStoresIds } from '../../Preferences/selectors';
import { getEvolution } from '../helpers';

/**
 * Get data about aggregated customers counts retrieved from api
 *
 * @return {Object}
 */
export const getCustomersCountsAggregatedEntities = ({ entities }) =>
  entities.customersCountsAggregated.entities;

/**
 * Get status about aggregated customers counts request
 * `none`, `loading`, `loaded` or `failed`
 *
 * @return {string}
 */
export const getCustomersCountsAggregatedStatus = ({ entities }) =>
  entities.customersCountsAggregated.status;

/**
 * If last attempt at fetching data resulted in an error, returns the error value
 *
 * @return {string}
 */
export const getCustomersCountsAggregatedError = ({ entities }) =>
  entities.customersCountsAggregated.error;

/**
 * If data is being fetched
 *
 * @return {boolean}
 */
export const areCustomersCountsAggregatedLoading = createSelector(
  [getCustomersCountsAggregatedStatus],
  status => status === 'loading',
);

/**
 * Get customers counts data of selected stores
 *
 * @return {Array}
 */
export const getDataOfSelectedStores = createSelector(
  [getCustomersCountsAggregatedEntities, getSelectedStoresIds],
  (entities, storeIds) =>
    entities && storeIds
      ? Object.keys(entities)
          .filter(id => storeIds.includes(parseInt(id, 10)))
          .map(id => ({ ...entities[id], id }))
      : [],
);

/**
 * Get customers counts data of selected stores and selected period
 * If there is no data, the api does not return the `selected` key, so we check the existence of the key
 *
 * @return {Array}
 */
export const getSelectedDataKpiOfSelectedStores = createSelector([getDataOfSelectedStores], data =>
  data.map(({ id, selected }) => ({
    ...{
      value: {
        customersCount:
          selected && selected.value.customersCount ? selected.value.customersCount : 0,
      },
    },
    id,
  })),
);

/**
 * Provides customers counts on selected stores and selected period
 *
 * @return {number}
 */
export const getSelectedCustomersCountsAggregated = createSelector(
  [getSelectedDataKpiOfSelectedStores],
  data => data.reduce((acc, { value }) => acc + value.customersCount, 0),
);

/**
 * Get customers counts data of selected stores and reference period
 * If there is no data, the api does not return the `reference` key, so we check the existence of the key
 *
 * @return {Array}
 */
export const getReferenceDataKpiOfSelectedStores = createSelector([getDataOfSelectedStores], data =>
  data.map(({ id, reference }) => ({
    ...{
      value: {
        customersCount:
          reference && reference.value.customersCount ? reference.value.customersCount : 0,
      },
    },
    id,
  })),
);

/**
 * Provides customers counts on selected stores and reference period
 *
 * @return {number}
 */
export const getReferenceCustomersCountsAggregated = createSelector(
  [getReferenceDataKpiOfSelectedStores],
  data => data.reduce((acc, { value }) => acc + value.customersCount, 0),
);

/**
 * Get evolution of customers counts from datas of reference period and selected period
 * Ratio between reference and selected datas
 *
 * @return {number}
 */
export const getEvolutionCustomersCountsAggregated = createSelector(
  [getReferenceCustomersCountsAggregated, getSelectedCustomersCountsAggregated],
  (reference, selected) => getEvolution(selected, reference),
);
